const STORAGE_KEY = 'yoda.quickCreate.placement';

export type QuickCreateMode = 'card' | 'support';

export type QuickCreatePlacement = {
    projectId?: string;
    boardId?: string;
    columnId?: string;
    mode?: QuickCreateMode;
};

export function loadQuickCreatePlacement(): QuickCreatePlacement {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw)
            return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object')
            return {};
        return {
            projectId: typeof parsed.projectId === 'string' ? parsed.projectId : undefined,
            boardId: typeof parsed.boardId === 'string' ? parsed.boardId : undefined,
            columnId: typeof parsed.columnId === 'string' ? parsed.columnId : undefined,
            mode: parsed.mode === 'support' || parsed.mode === 'card' ? parsed.mode : undefined,
        };
    } catch {
        return {};
    }
}

export function saveQuickCreatePlacement(placement: QuickCreatePlacement): void {
    try {
        const current = loadQuickCreatePlacement();
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, ...placement }));
    } catch {
        // Ignore quota / private mode errors.
    }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Split a free-text list of emails (comma / whitespace / semicolon). */
export function parseRequesterEmails(raw: string): string[] {
    const seen = new Set<string>();
    const emails: string[] = [];
    String(raw || '')
        .split(/[,;\s]+/)
        .map((part) => part.trim())
        .filter(Boolean)
        .forEach((email) => {
            const key = email.toLowerCase();
            if (!EMAIL_RE.test(email) || seen.has(key))
                return;
            seen.add(key);
            emails.push(email);
        });
    return emails;
}

export function isValidEmail(value: string): boolean {
    return EMAIL_RE.test(String(value || '').trim());
}

export type RequesterEmailSource = {
    email?: string | null;
    label?: string | null;
};

export type RequesterEmailSuggestion = {
    email: string;
    label: string;
};

/**
 * Ranked email suggestions for the support requester field.
 * Matches against email and optional label (name/company).
 */
export function suggestRequesterEmails(
    query: string,
    sources: RequesterEmailSource[],
    excludedEmails: string[] = [],
    limit = 8
): RequesterEmailSuggestion[] {
    const needle = String(query || '').trim().toLowerCase();
    if (needle.length < 1)
        return [];

    const excluded = new Set(
        excludedEmails.map((email) => String(email || '').trim().toLowerCase()).filter(Boolean)
    );
    const byEmail = new Map<string, RequesterEmailSuggestion>();

    (sources || []).forEach((source) => {
        const email = String(source?.email || '').trim();
        if (!EMAIL_RE.test(email))
            return;
        const key = email.toLowerCase();
        if (excluded.has(key) || byEmail.has(key))
            return;

        const label = String(source?.label || '').trim();
        const haystack = `${email} ${label}`.toLowerCase();
        if (!haystack.includes(needle))
            return;

        byEmail.set(key, {
            email,
            label: label && label.toLowerCase() !== key ? `${label} <${email}>` : email,
        });
    });

    return Array.from(byEmail.values())
        .sort((a, b) => {
            const aEmail = a.email.toLowerCase();
            const bEmail = b.email.toLowerCase();
            const aStarts = aEmail.startsWith(needle) ? 0 : 1;
            const bStarts = bEmail.startsWith(needle) ? 0 : 1;
            if (aStarts !== bStarts)
                return aStarts - bStarts;
            return aEmail.localeCompare(bEmail);
        })
        .slice(0, limit);
}
